import useFetch from './useFetch';
import {
  assetSelectionIssues,
  getApiErrorMessage,
  getAssetFormIdentities,
  getWorkAssetFormRelationships,
  type IWorkAssetFormRelationship,
} from '@moko/utils';

const EMPTY: IWorkAssetFormRelationship[] = [];

export const useWorkAssetForms = ({
  enabled,
  workFormDefinitionId,
  tenantId,
  assetIds,
  creates,
  imports,
  knownAssetForms,
}: {
  enabled: boolean;
  workFormDefinitionId?: number;
  tenantId?: number;
  assetIds: number[];
  creates: { name: string; formDefinitionId?: number }[];
  imports: { fileName: string; formDefinitionId?: number }[];
  knownAssetForms?: Readonly<Record<number, number>>;
}) => {
  const key = `${tenantId}:${workFormDefinitionId}`;
  const resource = useFetch<{
    key: string;
    relationships: IWorkAssetFormRelationship[];
  }>({
    queryKey: ['work-asset-forms', key],
    enabled: enabled && workFormDefinitionId != null && tenantId != null,
    queryFn: async (controller) => {
      if (workFormDefinitionId == null || tenantId == null)
        throw new Error(
          'Select a Work form and tenant before choosing assets.'
        );
      return {
        key,
        relationships: await getWorkAssetFormRelationships(
          controller,
          workFormDefinitionId,
          tenantId,
          true
        ),
      };
    },
  });
  const unresolvedIds = assetIds.filter(
    (id) => id > 0 && knownAssetForms?.[id] == null
  );
  const identityKey = `${key}:${unresolvedIds.join(',')}`;
  const needsIdentity =
    enabled &&
    !!resource.data?.relationships.length &&
    unresolvedIds.length > 0;
  const identity = useFetch<{ key: string; forms: Record<number, number> }>({
    queryKey: ['work-asset-identities', identityKey],
    enabled: needsIdentity && tenantId != null && resource.data?.key === key,
    queryFn: async (controller) => ({
      key: identityKey,
      forms: await getAssetFormIdentities(controller, unresolvedIds, tenantId!),
    }),
  });
  const ready =
    !enabled ||
    (resource.data?.key === key &&
      !resource.loading &&
      !resource.error &&
      (!needsIdentity ||
        (identity.data?.key === identityKey &&
          !identity.loading &&
          !identity.error)));
  const relationships =
    enabled && ready ? resource.data?.relationships ?? EMPTY : EMPTY;
  const error =
    enabled && (resource.error || (needsIdentity && identity.error))
      ? getApiErrorMessage(
          resource.error ?? identity.error,
          'Could not load permitted Asset forms.'
        )
      : undefined;
  const issues = ready
    ? assetSelectionIssues(relationships, assetIds, creates, imports, {
        ...(identity.data?.key === identityKey ? identity.data.forms : {}),
        ...knownAssetForms,
      })
    : [];
  const message =
    error ??
    (!ready
      ? 'Permitted Asset forms must be loaded before choosing assets or continuing.'
      : issues[0]);
  return {
    ready,
    relationships,
    issues,
    error,
    message,
    retry: () => {
      resource.refetch();
      if (needsIdentity) identity.refetch();
    },
    canProceed: ready && issues.length === 0,
  };
};
